import { useContext, useMemo } from "react";
import {
  Box,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Collapse,
} from "@mui/material";
import {
  Link,
  useLocation,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { LayoutContext } from "../../context/LayoutContext";
import { useSettings } from "../../context/SettingsContext";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { leadsAnalyticsMenu } from "../../config/leads.settings";
import { settingsMenu } from "../../config/settingsMenuConfig";
import { pipelinesAnalyticsMenu } from "../../config/pipelines.settings";
import { reportsMenu } from "../../config/reportsMenu";
import { chatMenu } from "../../config/chatMenu";
import useMediaQuery from "@mui/material/useMediaQuery";

// Get short label (first word or custom abbreviation)
const getShortLabel = (label) => {
  const customLabels = {
    "Dashboard": "Dash",
    "Pipelines": "Pipe",
    "Leads": "LEADS",
    "My Leads": "My Leads",
    "Reports": "Reports",
    "Settings": "Settings",
    "Follow-Ups": "Follows",
    "Untouched": "Untouched",
    "Recent": "Recent",
    "Users": "Users",
    "Teams": "Teams",
    // "Roles": "Roles",
    "Chat": "Chat",
    "Support Chat": "Support",
    "All Leads": "All Leads",
  };

  return customLabels[label] || label.split(" ")[0];
};

export default function SidebarItem({ item, overlay }) {
  const theme = useTheme();
  const { collapsed } = useContext(LayoutContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { lead_features, services, pipeline_features } = useSettings();

  const isExpandable = item.expandable === true;
  const isLeads = item.path === "/leads";
  const isSettings = item.path === "/settings";
  const isPipelines = item.path === "/pipelines";
  const isReports = item.path === "/reports";
  const isChat = item.path === "/chat";

  // Get view param early for active state checks
  const view = params.get("view") || "";

  // For Leads menu, don't mark as active when viewing "my-leads" (which is a separate menu item)
  const isActiveRoute = isLeads
    ? location.pathname.startsWith(item.path) && view !== "system:my-leads"
    : location.pathname.startsWith(item.path);

  const enabledSources = useMemo(
    () => (lead_features?.sources || []).filter((s) => s.enabled),
    [lead_features]
  );

  const enabledServices = useMemo(() => {
    const raw = services || [];
    return raw.map((s) => ({
      key: s.toLowerCase().replace(/\s+/g, "_"),
      label: s,
    }));
  }, [services]);

  const bookingItems = useMemo(() => {
    return (pipeline_features || [])
      .filter((f) => f.key.startsWith("bookings:") && f.enabled)
      .sort((a, b) => a.order - b.order)
      .map((b) => ({
        key: b.key.split(":")[1],
        label: b.label,
        fullKey: b.key,
      }));
  }, [pipeline_features]);

  const status = params.get("status") || "all";
  const [viewType, viewKey] = view.split(":");

  const pipelineView = params.get("view") || "";
  const [pipelineViewType, pipelineViewKey] = pipelineView.split(":");

  const goToLeads = (v) => {
    navigate(
      {
        pathname: "/leads",
        search: `?view=${v}&page=1&status=${status}`,
      },
      { state: { resetPage: true } }
    );
  };

  const goToSettings = (key) => {
    navigate(`/settings/${key}`);
  };

  const goToPipelines = (type, key) => {
    navigate({
      pathname: "/pipelines",
      search: `?view=${type}:${key}`,
    });
  };

  const hasPayments = useMemo(
    () =>
      (pipeline_features || []).some((f) => f.key === "payments" && f.enabled),
    [pipeline_features]
  );

  const hasBookings = useMemo(
    () =>
      (pipeline_features || []).some(
        (f) => f.key.startsWith("bookings:") && f.enabled
      ),
    [pipeline_features]
  );

  const filteredPipelinesMenu = useMemo(() => {
    return pipelinesAnalyticsMenu.filter((item) => {
      if (item.key === "bookings") return hasBookings;
      return true;
    });
  }, [hasBookings]);

  const filteredReportsMenu = useMemo(() => {
    return reportsMenu.filter((item) => {
      if (item.key === "payments") return hasPayments;
      if (item.key === "bookings") return hasBookings;
      return true;
    });
  }, [hasPayments, hasBookings]);

  // Check if this is the "My Leads" menu item
  const isMyLeads = item.path === "/leads?view=system:my-leads";

  if (!isExpandable) {
    // Check if the current route matches this item
    // For My Leads, check if view param is system:my-leads
    const active = isMyLeads
      ? location.pathname === "/leads" && view === "system:my-leads"
      : item.path.includes('?')
      ? location.pathname + location.search === item.path
      : location.pathname === item.path || location.pathname.startsWith(item.path + "/");

    // For My Leads, use onClick navigation to ensure proper state reset
    const handleClick = isMyLeads
      ? (e) => {
          e.preventDefault();
          navigate({
            pathname: "/leads",
            search: "?view=system:my-leads&page=1&status=all",
          }, {
            state: { resetPage: true },
          });
        }
      : undefined;

    return (
      <ListItemButton
        component={isMyLeads ? "div" : Link}
        to={isMyLeads ? undefined : item.path}
        onClick={handleClick}
        className={active ? "active" : undefined}
        sx={{
          mx: 1,
          mt: 1,
          py: 1.4,
          gap: collapsed && !overlay ? 0 : 2,
          borderRadius: 2,
          color: "#fff",
          flexDirection: collapsed && !overlay ? "column" : "row",
          alignItems: "center",
          cursor: "pointer",
          "&.active": {
            bgcolor: `${theme.sidebar.active} !important`,
            fontWeight: 900,
          },
        }}
      >
        <ListItemIcon
          sx={{
            color: "#fff",
            minWidth: 0,
            mr: collapsed && !overlay ? 0 : 2,
            mb: collapsed && !overlay ? 0.5 : 0,
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={collapsed && !overlay ? getShortLabel(item.label) : item.label}
          sx={{
            "& .MuiListItemText-primary": {
              fontSize: collapsed && !overlay ? "10px" : "14px",
              textAlign: collapsed && !overlay ? "center" : "left",
              lineHeight: 1.2,
            },
          }}
        />
      </ListItemButton>
    );
  }

  const mainButton = (
    <ListItemButton
      onClick={() => {
        if (item.path === "/chat") {
          navigate(chatMenu[0].path);
        } else if (item.path === "/leads") {
          // Navigate to "All Leads" when clicking the Leads menu
          navigate({
            pathname: "/leads",
            search: "?view=system:all&page=1&status=all",
          }, {
            state: { resetPage: true },
          });
        } else {
          navigate(item.path);
        }
      }}
      className={isActiveRoute ? "active" : undefined}
      sx={{
        mx: 1,
        mt: 1,
        py: 1.4,
        gap: collapsed && !overlay ? 0 : 2,
        borderRadius: collapsed && !overlay ? 3 : 2,
        color: "#fff",
        flexDirection: collapsed && !overlay ? "column" : "row",
        alignItems: "center",
        "&.active": {
          bgcolor: collapsed && !overlay
            ? `${theme.palette.primary.main}25`
            : `${theme.sidebar.active} !important`,
          fontWeight: 900,
          borderRight: collapsed && !overlay ? `3px solid ${theme.palette.primary.main}` : "none",
        },
      }}
    >
      <ListItemIcon
        sx={{
          color: "#fff",
          minWidth: 0,
          mr: collapsed && !overlay ? 0 : 2,
          mb: collapsed && !overlay ? 0.5 : 0,
        }}
      >
        {item.icon}
      </ListItemIcon>
      <ListItemText
        primary={collapsed && !overlay ? getShortLabel(item.label) : item.label}
        sx={{
          "& .MuiListItemText-primary": {
            fontSize: collapsed && !overlay ? "10px" : "14px",
            textAlign: collapsed && !overlay ? "center" : "left",
            lineHeight: 1.2,
          },
        }}
      />
      {(!collapsed || overlay) && (
        <>
          {isActiveRoute ? (
            <KeyboardArrowDownIcon />
          ) : (
            <KeyboardArrowRightIcon />
          )}
        </>
      )}
    </ListItemButton>
  );

  const menu = isLeads
    ? leadsAnalyticsMenu
    : isPipelines
    ? filteredPipelinesMenu
    : isReports
    ? filteredReportsMenu
    : isChat
    ? chatMenu
    : settingsMenu;

  return (
    <Box>
      {mainButton}

      <Collapse in={isActiveRoute} timeout="auto" unmountOnExit>
        {/* Collapsed mode: show dots */}
        {collapsed && !overlay && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 1 }}>
            {menu.map((sub, i) => {
              const isSources = isLeads && sub.key === "sources";
              const isServices = sub.key === "services" && (isLeads || isPipelines);
              const isBookings = isPipelines && sub.key === "bookings";

              const isSubActive = isLeads
                ? isSources
                  ? viewType === "source"
                  : isServices
                  ? viewType === "service"
                  : viewType === "system" && (
                      sub.key === "all-leads" ? viewKey === "all" : viewKey === sub.key
                    )
                : isPipelines
                ? isBookings
                  ? pipelineViewType === "system" &&
                    pipelineViewKey?.startsWith("bookings")
                  : isServices
                  ? pipelineViewType === "service"
                  : pipelineViewType === "system" && pipelineViewKey === sub.key
                : isSettings
                ? location.pathname === `/settings/${sub.key}`
                : location.pathname.startsWith(sub.path);

              return (
                <Box
                  key={sub.key}
                  onClick={() => {
                    if (isLeads) {
                      if (isSources && enabledSources[0]) {
                        goToLeads(`source:${enabledSources[0].key}`);
                      } else if (isServices && enabledServices[0]) {
                        goToLeads(`service:${enabledServices[0].key}`);
                      } else if (sub.key === "all-leads") {
                        goToLeads(`system:all`);
                      } else {
                        goToLeads(`system:${sub.key}`);
                      }
                    } else if (isPipelines) {
                      if (isBookings && bookingItems[0]) {
                        goToPipelines("system", bookingItems[0].fullKey);
                      } else if (isServices && enabledServices[0]) {
                        goToPipelines("service", enabledServices[0].key);
                      } else {
                        goToPipelines("system", sub.key);
                      }
                    } else if (isSettings) {
                      goToSettings(sub.key);
                    } else if (isReports || isChat) {
                      navigate(sub.path);
                    }
                  }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: "pointer",
                    py: 1,
                    position: "relative",
                    zIndex: 1,
                    "&:hover .dot": {
                      bgcolor: theme.palette.primary.main,
                      transform: "scale(1.2)",
                    },
                  }}
                >
                  {/* Dot */}
                  <Box
                    className="dot"
                    sx={{
                      width: isSubActive ? 10 : 8,
                      height: isSubActive ? 10 : 8,
                      borderRadius: "50%",
                      bgcolor: isSubActive ? theme.palette.primary.main : "rgba(255,255,255,0.4)",
                      mb: 0.5,
                      transition: "all 0.2s ease",
                    }}
                  />
                  {/* Label */}
                  <Typography
                    sx={{
                      fontSize: 9,
                      color: isSubActive ? "#fff" : "rgba(255,255,255,0.7)",
                      fontWeight: isSubActive ? 700 : 400,
                      textAlign: "center",
                      lineHeight: 1.2,
                    }}
                  >
                    {getShortLabel(sub.label)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Expanded mode: regular list items */}
        {(!collapsed || overlay) && menu.map((sub, i) => {
          const showSection =
            sub.section !== menu[i - 1]?.section;

          const isSources = isLeads && sub.key === "sources";
          const isServices = sub.key === "services" && (isLeads || isPipelines);
          const isBookings = isPipelines && sub.key === "bookings";

          const isParentActive = isLeads
            ? isSources
              ? viewType === "source"
              : isServices
              ? viewType === "service"
              : viewType === "system" && (
                  sub.key === "all-leads" ? viewKey === "all" : viewKey === sub.key
                )
            : isPipelines
            ? isBookings
              ? pipelineViewType === "system" &&
                pipelineViewKey?.startsWith("bookings")
              : isServices
              ? pipelineViewType === "service"
              : pipelineViewType === "system" && pipelineViewKey === sub.key
            : isSettings
            ? location.pathname === `/settings/${sub.key}`
            : location.pathname.startsWith(sub.path);

          return (
            <Box key={sub.key}>
              {showSection && (
                <Typography fontSize={11} sx={{ opacity: 0.6, mt: 2, ml: 6 }}>
                  {sub.section}
                </Typography>
              )}

              <ListItemButton
                className={isParentActive ? "active" : undefined}
                onClick={() => {
                  if (isLeads) {
                    if (isSources && enabledSources[0]) {
                      goToLeads(`source:${enabledSources[0].key}`);
                    } else if (isServices && enabledServices[0]) {
                      goToLeads(`service:${enabledServices[0].key}`);
                    } else if (sub.key === "all-leads") {
                      goToLeads(`system:all`);
                    } else {
                      goToLeads(`system:${sub.key}`);
                    }
                  } else if (isPipelines) {
                    if (isBookings && bookingItems[0]) {
                      goToPipelines("system", bookingItems[0].fullKey);
                    } else if (isServices && enabledServices[0]) {
                      goToPipelines("service", enabledServices[0].key);
                    } else {
                      goToPipelines("system", sub.key);
                    }
                  } else if (isSettings) {
                    goToSettings(sub.key);
                  } else if (isReports || isChat) {
                    navigate(sub.path);
                  }
                }}
                sx={{
                  ml: 5,
                  mr: 1,
                  mt: 1,
                  py: 1.4,
                  gap: 2,
                  borderRadius: 2,
                  color: "#fff",
                  flexDirection: "row",
                  alignItems: "center",
                  "&.active": {
                    bgcolor: `${theme.sidebar.active} !important`,
                    fontWeight: 900,
                  },
                }}
              >
                <ListItemIcon sx={{ color: "#fff", minWidth: 0, mr: 2 }}>
                  {sub.icon}
                </ListItemIcon>
                <ListItemText
                  primary={sub.label}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: "14px",
                      textAlign: "left",
                      lineHeight: 1.2,
                    },
                  }}
                />
                {(isSources || isServices || isBookings) &&
                  (viewType ===
                    (isSources
                      ? "source"
                      : isServices
                      ? "service"
                      : "system") &&
                  (isBookings
                    ? pipelineViewKey?.startsWith("bookings")
                    : true) ? (
                    <KeyboardArrowDownIcon />
                  ) : (
                    <KeyboardArrowRightIcon />
                  ))}
              </ListItemButton>

              {isSources && (
                <Collapse
                  in={viewType === "source"}
                  timeout="auto"
                  unmountOnExit
                >
                  {enabledSources.map((src) => (
                    <ListItemButton
                      key={src.key}
                      onClick={() => {
                        goToLeads(`source:${src.key}`);
                      }}
                      className={viewKey === src.key ? "active" : undefined}
                      sx={{
                        ml: 8,
                        mr: 1,
                        mt: 0.3,
                        py: 1,
                        borderRadius: 2,
                        textTransform: "capitalize",
                        color: "#fff",
                        "&.active": {
                          bgcolor: `${theme.sidebar.active} !important`,
                          fontWeight: 700,
                        },
                      }}
                    >
                      <ListItemText
                        primary={src.label}
                        sx={{
                          "& .MuiListItemText-primary": {
                            fontSize: "14px",
                            textAlign: "left",
                            lineHeight: 1.2,
                          },
                        }}
                      />
                    </ListItemButton>
                  ))}
                </Collapse>
              )}

              {isServices && (
                <Collapse
                  in={
                    ((isLeads && viewType === "service") ||
                      (isPipelines && pipelineViewType === "service"))
                  }
                  timeout="auto"
                  unmountOnExit
                >
                  {enabledServices.map((srv) => (
                    <ListItemButton
                      key={srv.key}
                      onClick={() => {
                        isLeads
                          ? goToLeads(`service:${srv.key}`)
                          : goToPipelines("service", srv.key);
                      }}
                      className={viewKey === srv.key ? "active" : undefined}
                      sx={{
                        ml: 8,
                        mr: 1,
                        mt: 0.3,
                        py: 1,
                        borderRadius: 2,
                        color: "#fff",
                        "&.active": {
                          bgcolor: `${theme.sidebar.active} !important`,
                          fontWeight: 700,
                        },
                        textTransform: "capitalize",
                      }}
                    >
                      <ListItemText
                        primary={srv.label}
                        sx={{
                          "& .MuiListItemText-primary": {
                            fontSize: "14px",
                            textAlign: "left",
                            lineHeight: 1.2,
                          },
                        }}
                      />
                    </ListItemButton>
                  ))}
                </Collapse>
              )}

              {isBookings && (
                <Collapse
                  in={
                    pipelineViewType === "system" &&
                    pipelineViewKey?.startsWith("bookings")
                  }
                  timeout="auto"
                  unmountOnExit
                >
                  {bookingItems.map((bk) => (
                    <ListItemButton
                      key={bk.key}
                      onClick={() => {
                        goToPipelines("system", bk.fullKey);
                      }}
                      className={
                        view === `system:${bk.fullKey}` ? "active" : undefined
                      }
                      sx={{
                        ml: 8,
                        mr: 1,
                        mt: 0.3,
                        py: 1,
                        textTransform: "capitalize",
                        borderRadius: 2,
                        color: "#fff",
                        "&.active": {
                          bgcolor: `${theme.sidebar.active} !important`,
                          fontWeight: 700,
                        },
                      }}
                    >
                      <ListItemText
                        primary={bk.label}
                        sx={{
                          "& .MuiListItemText-primary": {
                            fontSize: "14px",
                            textAlign: "left",
                            lineHeight: 1.2,
                          },
                        }}
                      />
                    </ListItemButton>
                  ))}
                </Collapse>
              )}
            </Box>
          );
        })}
      </Collapse>
    </Box>
  );
}
